import type { KnowledgeBaseAttachment } from "@repo/app-types/entities/knowledgeBase";
import { maybeGetKnowledgeBaseExternalProvider } from "@repo/app-types/entities/knowledgeBaseSync";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Link } from "@repo/ui/Link/routerLink";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { TableSkeletonContent } from "@repo/ui/Skeletons/skeletons";
import { IconFile } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useGetKnowledgeBaseAttachmentsQuery,
  useGetKnowledgeBaseQuery,
} from "../../../api/entities/knowledgeBase.js";
import { appPaths } from "../../../app/appPaths.js";
import { requireRouteParam } from "../../../app/routeParams.js";
import { PageBody, PageLayout } from "../../../components/layout/pageLayout.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { KnowledgeBaseAttachmentEditor } from "./knowledgeBaseAttachmentEditor.js";
import { KnowledgeBaseDocumentsUploader } from "./knowledgeBaseDocumentsUploader.js";
import { KnowledgeBaseNameEditor } from "./knowledgeBaseNameEditor.js";
import { KnowledgeBaseSyncPill } from "./knowledgeBaseSyncPill.js";

interface KnowledgeBaseDocumentsListProps {
  attachments: readonly KnowledgeBaseAttachment[];
  knowledgeBaseId: string;
  isLoading: boolean;
  isSynced: boolean;
}

const KnowledgeBaseDocumentsList: FC<KnowledgeBaseDocumentsListProps> = (
  props,
) => {
  const { attachments, knowledgeBaseId, isLoading, isSynced } = props;

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-md border border-border-light bg-white">
        <TableSkeletonContent rowCount={3} />
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="rounded-md border border-border-light bg-white px-3 py-9 text-center text-sm font-semibold text-foreground">
        {isSynced ? "No documents synced yet." : "No documents uploaded yet."}
      </div>
    );
  }

  return (
    <ul
      aria-label="Knowledge base documents"
      className="m-0 list-none divide-y divide-border-light overflow-hidden rounded-md border border-border-light bg-white p-0"
    >
      {attachments.map((attachment) => {
        const title = attachment.title || "Untitled document";
        const updatedAt = attachment.updated_at ?? attachment.created_at;
        const fileName = attachment.sync ? null : attachment.file_name || null;

        return (
          <li key={attachment.id}>
            <Link
              aria-label={`${attachment.sync ? "View" : "Edit"} ${title}`}
              className="group block w-full cursor-pointer px-4 py-4 text-left transition-[background-color] hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
              href={appPaths.settingsKnowledgeBaseAttachment.build({
                knowledgeBaseId,
                attachmentId: attachment.id,
              })}
            >
              <span className="block truncate text-sm font-semibold text-foreground">
                {title}
              </span>

              {attachment.description ? (
                <span
                  className="mt-1 line-clamp-2 text-sm leading-5 text-muted-foreground"
                  title={attachment.description}
                >
                  {attachment.description}
                </span>
              ) : (
                <span className="mt-1 block text-sm leading-5 text-muted-foreground">
                  No description
                </span>
              )}

              <span className="mt-3 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <time dateTime={updatedAt}>
                  Last updated {formatKnowledgeBaseDocumentDate(updatedAt)}
                </time>
                {fileName ? (
                  <span
                    className="flex min-w-0 items-center gap-1.5"
                    title={fileName}
                  >
                    <IconFile
                      aria-hidden="true"
                      className="size-3.5 shrink-0"
                    />
                    <span className="truncate">{fileName}</span>
                  </span>
                ) : null}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

function formatKnowledgeBaseDocumentDate(timestamp: string): string {
  const dateTime = DateTime.fromISO(timestamp);

  return dateTime.isValid
    ? dateTime.toLocaleString(DateTime.DATETIME_MED)
    : timestamp;
}

export function KnowledgeBaseDetailsPage() {
  const routeParams = useParams();
  const knowledgeBaseId = requireRouteParam(routeParams, "knowledgeBaseId");
  const attachmentId = routeParams.attachmentId;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    data: knowledgeBase,
    isLoading: isLoadingKnowledgeBase,
    isError: isErrorKnowledgeBase,
  } = useGetKnowledgeBaseQuery(knowledgeBaseId);

  const { data: attachments = [], isLoading: isLoadingAttachments } =
    useGetKnowledgeBaseAttachmentsQuery(knowledgeBaseId);

  const isLoading = isLoadingKnowledgeBase || isLoadingAttachments;
  const isError = isErrorKnowledgeBase;

  // Sync-owned KBs (e.g. from Kustomer) still allow a customer-facing name,
  // but documents remain externally managed and read-only.
  const externalProvider = maybeGetKnowledgeBaseExternalProvider(
    knowledgeBase?.metadata,
  );
  const isSynced = Boolean(externalProvider);

  const handleUploadSuccess = (upload: { id: string; name: string }) => {
    showNotification(dispatch, {
      id: `upload-success-${upload.name}-${Date.now()}`,
      primaryMessage: `Successfully uploaded ${upload.name}`,
      type: NotificationType.SUCCESS,
    });
  };

  const handleUploadError = (fileName: string, error: any) => {
    showNotification(dispatch, {
      id: `upload-error-${fileName}-${Date.now()}`,
      primaryMessage: `Failed to upload ${fileName}: ${error.data?.error || error.message || "Unknown error"}`,
      type: NotificationType.FAILURE,
    });
  };

  if (attachmentId) {
    return (
      <KnowledgeBaseAttachmentEditor
        key={attachmentId}
        knowledgeBaseId={knowledgeBaseId}
        attachmentId={attachmentId}
        knowledgeBaseAttachments={attachments}
        isReadOnly={knowledgeBase ? isSynced : undefined}
        onClose={() =>
          navigate(appPaths.settingsKnowledgeBase.build({ knowledgeBaseId }))
        }
      />
    );
  }

  return (
    <PageLayout>
      <PageHeader
        className="pb-3 after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-6 after:h-6 after:bg-linear-to-b after:from-card after:to-transparent after:content-['']"
        width={PageWidth.Standard}
        title={
          isError ? (
            "Error loading knowledge base"
          ) : knowledgeBase ? (
            <KnowledgeBaseNameEditor
              key={knowledgeBase.id}
              knowledgeBase={knowledgeBase}
            />
          ) : (
            "Knowledge base"
          )
        }
        isLoading={isLoadingKnowledgeBase}
        contentClassName={
          knowledgeBase && !isError ? "pr-4 pl-2 sm:pr-6 sm:pl-4" : undefined
        }
        actions={
          externalProvider ? (
            <KnowledgeBaseSyncPill
              provider={externalProvider.provider}
              lastSyncedAt={externalProvider.last_synced_at}
            />
          ) : undefined
        }
      />

      <PageBody width={PageWidth.Standard}>
        {isError ? (
          <div className="text-destructive">
            Failed to load knowledge base. Please try again.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Documents uploader section (synced KBs don't accept uploads) */}
            {!isLoading && knowledgeBase && !isSynced ? (
              <div>
                <Label size={Size.SMALL}>Upload documents</Label>
                <div className="mt-2">
                  <KnowledgeBaseDocumentsUploader
                    knowledgeBaseId={knowledgeBase.id}
                    onUploadSuccess={handleUploadSuccess}
                    onUploadError={handleUploadError}
                  />
                </div>
              </div>
            ) : null}

            {/* Documents list */}
            <div>
              <Label size={Size.SMALL}>Documents</Label>
              <div className="mt-2">
                <KnowledgeBaseDocumentsList
                  attachments={attachments}
                  knowledgeBaseId={knowledgeBaseId}
                  isLoading={isLoading}
                  isSynced={isSynced}
                />
              </div>
            </div>
          </div>
        )}
      </PageBody>
    </PageLayout>
  );
}
